import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, TrendingUp, ExternalLink } from 'lucide-react';
import { getIntelligentLinks, LinkSuggestion } from '@/utils/intelligentLinking';

interface IntelligentInternalLinksProps {
  currentPage: string;
  content: string;
  pageType: 'blog' | 'service' | 'location' | 'resource';
  keywords?: string[];
  maxLinks?: number;
  variant?: 'sidebar' | 'inline' | 'footer';
  className?: string;
}

const IntelligentInternalLinks: React.FC<IntelligentInternalLinksProps> = ({
  currentPage,
  content,
  pageType,
  keywords = [],
  maxLinks = 5,
  variant = 'sidebar',
  className = ''
}) => {
  const linkSuggestions = getIntelligentLinks(currentPage, content, pageType, keywords, maxLinks);

  if (linkSuggestions.length === 0) {
    return null;
  }

  const renderSidebarVariant = () => (
    <Card className={`border-brand-trophy-gold/30 shadow-lg ${className}`}>
      <CardHeader>
        <CardTitle className="text-lg font-bold text-brand-charcoal-gray flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-brand-trophy-gold" />
          Related Services & Resources
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {linkSuggestions.map((suggestion, index) => (
          <div key={index} className="p-3 rounded-lg border border-gray-200 hover:border-brand-trophy-gold/50 transition-colors">
            <div className="flex items-start justify-between mb-2">
              <Badge 
                variant={suggestion.priority === 'high' ? 'default' : 'secondary'}
                className={suggestion.priority === 'high' ? 'bg-brand-safety-orange text-white' : ''}
              >
                {suggestion.type}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {suggestion.priority}
              </Badge>
            </div>
            <Link 
              to={suggestion.url}
              className="block font-medium text-brand-charcoal-gray hover:text-brand-trophy-gold transition-colors mb-1"
            >
              {suggestion.title}
            </Link>
            <p className="text-sm text-gray-600 mb-2">{suggestion.context}</p>
            <Link to={suggestion.url}>
              <Button variant="ghost" size="sm" className="p-0 h-auto text-brand-trophy-gold hover:text-brand-safety-orange">
                Learn More <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </Link>
          </div>
        ))}
      </CardContent>
    </Card>
  );

  const renderInlineVariant = () => (
    <div className={`bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-lg my-8 ${className}`}>
      <h3 className="text-lg font-semibold mb-4 text-brand-charcoal-gray flex items-center gap-2">
        <TrendingUp className="h-5 w-5 text-brand-trophy-gold" />
        You Might Also Be Interested In
      </h3>
      <div className="grid md:grid-cols-2 gap-4">
        {linkSuggestions.slice(0, 4).map((suggestion, index) => (
          <Link 
            key={index}
            to={suggestion.url}
            className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-brand-trophy-gold/50 hover:shadow-md transition-all group"
          >
            <div>
              <div className="font-medium text-brand-charcoal-gray group-hover:text-brand-trophy-gold transition-colors">
                {suggestion.title}
              </div>
              <div className="text-sm text-gray-600 mt-1">{suggestion.context}</div>
            </div>
            <ArrowRight className="h-4 w-4 text-brand-trophy-gold group-hover:translate-x-1 transition-transform" />
          </Link>
        ))}
      </div>
    </div>
  );

  const renderFooterVariant = () => (
    <div className={`border-t border-gray-200 pt-8 mt-12 ${className}`}>
      <h3 className="text-xl font-bold mb-6 text-brand-charcoal-gray">Continue Exploring</h3>
      <div className="grid md:grid-cols-3 gap-6">
        {linkSuggestions.slice(0, 3).map((suggestion, index) => (
          <Card key={index} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-4">
              <Badge className="mb-3" variant={suggestion.priority === 'high' ? 'default' : 'secondary'}>
                {suggestion.type}
              </Badge>
              <h4 className="font-semibold mb-2 text-brand-charcoal-gray">{suggestion.title}</h4>
              <p className="text-sm text-gray-600 mb-4">{suggestion.context}</p>
              <Link to={suggestion.url}>
                <Button variant="outline" size="sm" className="w-full border-brand-trophy-gold text-brand-trophy-gold hover:bg-brand-trophy-gold hover:text-white">
                  Learn More
                  <ExternalLink className="ml-2 h-3 w-3" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  switch (variant) {
    case 'inline':
      return renderInlineVariant();
    case 'footer':
      return renderFooterVariant();
    default:
      return renderSidebarVariant();
  }
};

export default IntelligentInternalLinks;